/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Executive Studio Layout Template (Task 4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from '../../context/ThemeProvider';
import { useAuthStore } from '../../stores/useAuthStore';
import { useI18nStore } from '../../stores/useI18nStore';
import { useUiStore } from '../../stores/useUiStore';
import { resolvePlatformDomains } from '@sofia/shared-types';
import { LOGO_VARIANTS } from '@sofia/assets';
import { SophiaIcon } from '@sofia/assets';
import { NAVIGATION_SECTIONS } from '../../navigation/navigation-items';
import { WorkspaceSwitcher } from '../../components/workspace/WorkspaceSwitcher';
import { UserProfileDropdown } from '../../components/header/UserProfileDropdown';
import { WalletBadge } from '../../components/wallet/WalletBadge';
import { LanguageSwitcher } from '../../components/i18n/LanguageSwitcher';
import { ThemeSwitcher } from '../../components/theme/ThemeSwitcher';
import { CreateWorkspaceModal } from '../../components/workspace/CreateWorkspaceModal';
import { CommandPalette } from '../../components/command/CommandPalette';
import { TopupModal } from '../../components/wallet/TopupModal';
import { PaymentCheckoutModal } from '../../components/billing/PaymentCheckoutModal';
import { InviteMemberModal } from '../../components/team/InviteMemberModal';
import { MobileDrawer } from '../../components/navigation/MobileDrawer';
import {
  Menu,
  Search,
  ChevronRight,
  ChevronDown,
  LayoutGrid,
  Crown,
} from 'lucide-react';
import type { DashboardLayoutProps } from '../../layouts/DashboardLayout';

export const ExecutiveLayout: React.FC<DashboardLayoutProps> = ({
  currentPath,
  onNavigate,
  children,
}) => {
  const { themeData, isDark } = useTheme();
  const { user, clearSession } = useAuthStore();
  const { t } = useI18nStore();
  const { setMobileDrawerOpen, openCommandPalette } = useUiStore();

  const [megaDropdownOpen, setMegaDropdownOpen] = useState(false);
  const megaDropdownContainerRef = useRef<HTMLDivElement>(null);

  const branding = themeData?.branding;
  const siteName = branding?.siteName || 'Sophia Executive';
  const defaultLogo = isDark ? LOGO_VARIANTS.fullDark : LOGO_VARIANTS.fullLight;
  const logoUrl =
    (isDark ? branding?.logoDarkUrl : branding?.logoLightUrl) ||
    branding?.logoLightUrl ||
    branding?.logoDarkUrl ||
    defaultLogo;

  const handleExitImpersonation = () => {
    clearSession();
    const domains = resolvePlatformDomains();
    window.location.href = domains.adminOrigin;
  };

  // Close Mega Dropdown on outside click or Escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        megaDropdownContainerRef.current &&
        !megaDropdownContainerRef.current.contains(event.target as Node)
      ) {
        setMegaDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && megaDropdownOpen) {
        setMegaDropdownOpen(false);
      }
    };

    if (megaDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [megaDropdownOpen]);

  // Find active navigation item and section
  const activeNav = useMemo(() => {
    for (const section of NAVIGATION_SECTIONS) {
      const foundItem = section.items.find((item) => item.path === currentPath);
      if (foundItem) {
        return { section, item: foundItem };
      }
    }
    return {
      section: NAVIGATION_SECTIONS[0],
      item: NAVIGATION_SECTIONS[0]?.items[0],
    };
  }, [currentPath]);

  // Synchronize document title and favicon
  useEffect(() => {
    const pageTitle = activeNav?.item?.label ?? 'Executive';
    document.title = `${pageTitle} | ${siteName} (Executive Studio)`;

    if (themeData?.branding?.faviconUrl) {
      let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0]?.appendChild(link);
      }
      link.href = themeData.branding.faviconUrl;
    }
  }, [activeNav, siteName, themeData]);

  return (
    <div className="template-executive min-h-screen bg-background-base flex flex-col font-sans text-foreground transition-colors duration-200">
      {/* 0. Impersonation Top Amber Banner */}
      {user?.isImpersonated && (
        <aside
          aria-label="Impersonation Warning Banner"
          className="bg-amber-500 text-amber-950 px-4 py-2 text-xs font-semibold flex items-center justify-between sticky top-0 z-50 shadow-md border-b border-amber-600/30 shrink-0"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm">⚠️</span>
            <span>{t('impersonation.bannerNotice', { email: user.email })}</span>
          </div>
          <button
            type="button"
            onClick={handleExitImpersonation}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-950 text-amber-100 hover:bg-black font-semibold text-xs transition-colors ml-4 shadow-sm"
          >
            <span>{t('impersonation.exit')}</span>
          </button>
        </aside>
      )}

      {/* 1. Executive Top Navbar (Single-tier Horizontal Grid Architecture) */}
      <header className="sticky top-0 z-40 border-b border-surface-border bg-surface/90 backdrop-blur-xl px-4 sm:px-6 lg:px-10 xl:px-12 shadow-sm select-none">
        <div className="max-w-[1720px] w-full mx-auto h-16 flex items-center justify-between gap-4">
          {/* Brand & Mobile Trigger */}
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={() => setMobileDrawerOpen(true)}
              className="lg:hidden p-2 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
              aria-label={t('header.openMenu')}
            >
              <Menu className="w-5 h-5" />
            </button>

            <button
              type="button"
              onClick={() => onNavigate('/workspace/dashboard')}
              className="flex items-center gap-2.5 text-left group"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={siteName}
                  className="h-8 max-w-[130px] object-contain transition-transform group-hover:scale-105"
                  onError={(e) => {
                    (e.currentTarget as HTMLElement).style.display = 'none';
                    const fb = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fb) fb.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-9 h-9 rounded-xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center text-white font-bold text-sm shadow-glow-primary/20 shrink-0"
                style={logoUrl ? { display: 'none' } : undefined}
              >
                <Crown className="w-4 h-4 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-black text-sm tracking-tight text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5">
                  <span>{siteName}</span>
                  <span className="px-1.5 py-0.2 rounded-md text-[9px] font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                    Executive
                  </span>
                </span>
                <span className="text-[10px] text-foreground-muted font-mono tracking-wider uppercase">
                  Top-Nav Command Matrix
                </span>
              </div>
            </button>
          </div>

          {/* Center Navigation Matrix: Studio Mega Menu Dropdown + Quick Section Tabs */}
          <div className="hidden lg:flex items-center gap-2 flex-1 justify-start ml-2">
            {/* Studio Mega Dropdown Trigger */}
            <div className="relative" ref={megaDropdownContainerRef}>
              <button
                type="button"
                onClick={() => setMegaDropdownOpen((prev) => !prev)}
                aria-expanded={megaDropdownOpen}
                aria-haspopup="true"
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 border ${
                  megaDropdownOpen
                    ? 'bg-primary text-primary-fg border-primary shadow-sm'
                    : 'bg-surface-elevated/80 text-foreground hover:text-primary hover:bg-muted/80 border-surface-border'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="font-heading font-bold">{t('header.studiosMegaMenu')}</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    megaDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Floating Studio Mega Dropdown Panel */}
              {megaDropdownOpen && (
                <div
                  role="dialog"
                  aria-label={t('header.studiosMegaMenu')}
                  className="absolute left-0 top-full mt-2 w-[760px] lg:w-[960px] xl:w-[1120px] max-w-[95vw] bg-surface/95 backdrop-blur-2xl border border-surface-border shadow-2xl rounded-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
                >
                  {/* Header Info */}
                  <div className="px-6 py-4 border-b border-surface-border bg-surface-elevated/40 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                        <LayoutGrid className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-xs font-bold font-heading text-foreground uppercase tracking-wider">
                          {t('header.allStudios')}
                        </h4>
                        <p className="text-[11px] text-foreground-muted">
                          {t('header.studiosMegaDesc')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setMegaDropdownOpen(false);
                          openCommandPalette();
                        }}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-surface-border bg-muted/40 hover:bg-muted text-[11px] text-foreground-muted hover:text-foreground transition-colors"
                      >
                        <Search className="w-3 h-3" />
                        <span>{t('header.quickSearch')}</span>
                        <kbd className="px-1 py-0.2 rounded bg-surface-elevated border border-surface-border text-[9px] font-mono">
                          ⌘K
                        </kbd>
                      </button>
                    </div>
                  </div>

                  {/* Grid of Navigation Sections (4 Columns) */}
                  <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-h-[65vh] overflow-y-auto">
                    {NAVIGATION_SECTIONS.map((section) => (
                      <div key={section.id} className="space-y-2">
                        <div className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-foreground-muted flex items-center justify-between border-b border-surface-border/50">
                          <span>{section.title}</span>
                          <span className="text-[10px] font-mono text-foreground-muted/70">
                            {section.items.length}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {section.items.map((item) => {
                            const isItemActive = item.path === currentPath;
                            const Icon = item.icon;

                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => {
                                  onNavigate(item.path);
                                  setMegaDropdownOpen(false);
                                }}
                                className={`w-full text-left px-2.5 py-2 rounded-xl transition-all flex items-start gap-2.5 group ${
                                  isItemActive
                                    ? 'bg-primary/10 border border-primary/20 text-primary font-semibold shadow-xs'
                                    : 'hover:bg-muted/60 text-foreground-muted hover:text-foreground border border-transparent'
                                }`}
                              >
                                <div
                                  className={`p-1.5 rounded-lg mt-0.5 shrink-0 transition-colors ${
                                    isItemActive
                                      ? 'bg-primary text-primary-fg'
                                      : 'bg-muted/70 text-foreground-muted group-hover:bg-primary/10 group-hover:text-primary'
                                  }`}
                                >
                                  {item.sophiaIcon ? (
                                    <SophiaIcon name={item.sophiaIcon} size={14} className="w-3.5 h-3.5" />
                                  ) : (
                                    <Icon className="w-3.5 h-3.5" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-medium truncate group-hover:text-foreground transition-colors">
                                      {item.label}
                                    </span>
                                    {item.badge && (
                                      <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold uppercase tracking-wide bg-accent/15 text-accent shrink-0">
                                        {item.badge}
                                      </span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <p className="text-[10px] text-foreground-muted/80 truncate mt-0.5 leading-tight">
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer Quick Shortcuts */}
                  <div className="px-6 py-3 border-t border-surface-border bg-surface-elevated/60 flex items-center justify-between text-xs text-foreground-muted">
                    <span className="text-[11px] font-medium">{t('header.quickShortcuts')}</span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          onNavigate('/workspace/dashboard');
                          setMegaDropdownOpen(false);
                        }}
                        className="hover:text-primary transition-colors text-[11px]"
                      >
                        {t('sidebar.items.dashboard.label')}
                      </button>
                      <span>•</span>
                      <button
                        type="button"
                        onClick={() => {
                          onNavigate('/workspace/documents');
                          setMegaDropdownOpen(false);
                        }}
                        className="hover:text-primary transition-colors text-[11px]"
                      >
                        {t('sidebar.items.documents.label')}
                      </button>
                      <span>•</span>
                      <button
                        type="button"
                        onClick={() => {
                          onNavigate('/workspace/settings');
                          setMegaDropdownOpen(false);
                        }}
                        className="hover:text-primary transition-colors text-[11px]"
                      >
                        {t('sidebar.items.workspaceSettings.label')}
                      </button>
                      <span>•</span>
                      <button
                        type="button"
                        onClick={() => {
                          onNavigate('/workspace/developer/keys');
                          setMegaDropdownOpen(false);
                        }}
                        className="hover:text-primary transition-colors text-[11px]"
                      >
                        {t('sidebar.items.apiKeys.label')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Section Tabs */}
            <nav className="flex items-center gap-1 bg-surface-elevated/80 p-1 rounded-xl border border-surface-border">
              {NAVIGATION_SECTIONS.map((section) => {
                const isSectionActive = section.id === activeNav?.section?.id;
                const primaryPath = section.items[0]?.path || '/workspace/documents';

                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => onNavigate(primaryPath)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${
                      isSectionActive
                        ? 'bg-primary text-primary-fg shadow-sm'
                        : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
                    }`}
                  >
                    <span>{section.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Header Controls (Right) */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Quick Command Palette Button */}
            <button
              type="button"
              onClick={openCommandPalette}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl border border-surface-border bg-muted/40 hover:bg-muted/70 text-xs text-foreground-muted transition-colors"
              title={t('header.searchOrRun')}
            >
              <Search className="w-3.5 h-3.5" />
              <span className="hidden md:inline">{t('header.quickSearch')}</span>
              <kbd className="px-1.5 py-0.5 rounded bg-surface-elevated border border-surface-border text-[10px] font-mono text-foreground-muted shadow-sm">
                ⌘K
              </kbd>
            </button>

            <LanguageSwitcher variant="header" />
            <ThemeSwitcher />
            <WalletBadge variant="header" />
            <WorkspaceSwitcher />
            <UserProfileDropdown onNavigate={onNavigate} />
          </div>
        </div>
      </header>

      {/* 2. Executive Ribbon / Sub-Navigation Toolbar */}
      {activeNav && activeNav.section && (
        <div className="bg-surface/60 backdrop-blur-md border-b border-surface-border px-4 sm:px-6 lg:px-10 xl:px-12 py-2 select-none">
          <div className="max-w-[1720px] w-full mx-auto flex flex-wrap items-center justify-between gap-3">
            {/* Left: Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
              <span className="font-semibold text-foreground uppercase tracking-wider text-[11px]">
                {activeNav.section.title}
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-surface-border" />
              <span className="text-primary font-medium">{activeNav.item?.label}</span>
            </div>

            {/* Right: Studio Shortcut Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 scrollbar-none">
              {activeNav.section.items.map((item) => {
                const isItemActive = item.path === currentPath;
                const Icon = item.icon;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onNavigate(item.path)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors shrink-0 ${
                      isItemActive
                        ? 'bg-primary/10 text-primary border border-primary/20 font-semibold'
                        : 'text-foreground-muted hover:text-foreground hover:bg-muted/60'
                    }`}
                  >
                    {item.sophiaIcon ? (
                      <SophiaIcon name={item.sophiaIcon} size={14} className="w-3.5 h-3.5" />
                    ) : (
                      <Icon className="w-3.5 h-3.5" />
                    )}
                    <span>{item.label}</span>
                    {item.badge && (
                      <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold uppercase bg-accent/15 text-accent">
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 3. Main Content Viewport (Expanded Horizontal Canvas) */}
      <main className="flex-1 max-w-[1720px] w-full mx-auto p-4 sm:p-6 lg:p-8 xl:p-10 space-y-6 overflow-y-auto animate-fade-in">
        {children}
      </main>

      {/* 4. Global Modals, Mobile Drawer & Command Palette */}
      <MobileDrawer currentPath={currentPath} onNavigate={onNavigate} />
      <CreateWorkspaceModal />
      <TopupModal />
      <PaymentCheckoutModal />
      <InviteMemberModal />
      <CommandPalette onNavigate={onNavigate} />
    </div>
  );
};
